/**
 * AssetsCard — fleet-status donut for the OEM Home strip (Phase 2).
 *
 * Ports the reference HomeOEM "Assets" card (AssetDonut + legend) to RNW + Crop
 * Circle tokens. The donut is a raw <svg> (web-only, like MapView's controls) —
 * proportional segments around one circle via strokeDasharray/offset, fleet
 * total in the middle, status legend on the right.
 *
 * Map-plottable: pressing the header toggles the map to plot individual assets
 * (clustered). Consumes `useHomeCards(range)`.
 */
import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { tokens, Skeleton, EmptyState, ErrorState } from '@gripp/shared-ui';
import { DashboardCardShell, ViewAllLink } from './dashboardCardShell';
import { useHomeCards, type HomeAssetStatusSlice } from '../hooks/useHomeCards';
import { HomeCardProps } from '../types';

// Slate ramp, darkest → lightest, matching the reference's slate-500→200 donut.
// Index by slice order (Deployed largest → Returned smallest).
const DONUT_COLORS = [
  tokens.colors.slate[500],
  tokens.colors.slate[400],
  tokens.colors.slate[300],
  tokens.colors.slate[200],
];

// Reference AssetDonut proportions (148/46) scaled to the strip card; a thick
// ring with a small empty centre (the fleet total is the card subtitle, not a
// centre label — matching gripp-os, which renders no centre number).
const DONUT_SIZE = 140;
const DONUT_STROKE = 44;

/** Proportional donut (raw SVG, web-only). Segments drawn around one circle. */
const AssetDonut: React.FC<{
  items: HomeAssetStatusSlice[];
  total: number;
}> = ({ items, total }) => {
  const radius = (DONUT_SIZE - DONUT_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;

  let acc = 0;
  const segments = items.map((s, i) => {
    const frac = total > 0 ? s.count / total : 0;
    const length = circumference * frac;
    const offset = -(circumference * (acc / Math.max(total, 1)));
    acc += s.count;
    return {
      key: s.status,
      length,
      offset,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
    };
  });

  return (
    <View style={{ width: DONUT_SIZE, height: DONUT_SIZE }}>
      <svg
        viewBox={`0 0 ${DONUT_SIZE} ${DONUT_SIZE}`}
        width={DONUT_SIZE}
        height={DONUT_SIZE}
        style={{ transform: 'rotate(-90deg)' }}
      >
        <circle
          cx={DONUT_SIZE / 2}
          cy={DONUT_SIZE / 2}
          r={radius}
          fill="none"
          stroke={tokens.colors.slate[100]}
          strokeWidth={DONUT_STROKE}
        />
        {segments.map((s) => (
          <circle
            key={s.key}
            cx={DONUT_SIZE / 2}
            cy={DONUT_SIZE / 2}
            r={radius}
            fill="none"
            stroke={s.color}
            strokeWidth={DONUT_STROKE}
            strokeDasharray={`${s.length} ${circumference - s.length}`}
            strokeDashoffset={s.offset}
          />
        ))}
      </svg>
    </View>
  );
};

export const AssetsCard: React.FC<HomeCardProps> = ({
  range,
  selected,
  onToggleSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useHomeCards(range);
  const assets = data?.assets;

  const title = t('home.assets.title', 'Assets');
  const action = (
    <ViewAllLink
      label={t('home.cards.viewAll', 'View all')}
      onPress={() => navigate('/assets')}
      testID="assets-card-view-all"
    />
  );

  if (isLoading) {
    return (
      <DashboardCardShell title={title} action={action} testID="assets-card"
        loadingSlot={<Skeleton height={120} radius={tokens.borderRadius.full} style={styles.loadingDonut} />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (error) {
    return (
      <DashboardCardShell title={title} action={action} testID="assets-card"
        errorSlot={<ErrorState title={t('home.cards.error', 'Could not load data')} testID="assets-card-error" />}
      >
        {null}
      </DashboardCardShell>
    );
  }
  if (!assets || assets.total === 0) {
    return (
      <DashboardCardShell title={title} action={action} testID="assets-card"
        emptySlot={<EmptyState title={t('home.assets.empty', 'No assets for this period')} testID="assets-card-empty" />}
      >
        {null}
      </DashboardCardShell>
    );
  }

  return (
    <DashboardCardShell
      title={title}
      subtitle={String(assets.total)}
      action={action}
      testID="assets-card"
      onHeaderPress={onToggleSelect}
      selected={selected}
      headerPressLabel={t('home.assets.plot', 'Plot assets on the map')}
    >
      <View style={styles.body}>
        <AssetDonut items={assets.items} total={assets.total} />
        <View style={styles.legend}>
          {assets.items.map((s, i) => (
            <View key={s.status} style={styles.legendRow}>
              <View
                style={[
                  styles.legendDot,
                  { backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] },
                ]}
              />
              <Text style={styles.legendLabel} numberOfLines={1}>{s.status}</Text>
              <Text style={styles.legendCount}>{s.count}</Text>
            </View>
          ))}
        </View>
      </View>
    </DashboardCardShell>
  );
};

AssetsCard.displayName = 'AssetsCard';

const styles = StyleSheet.create({
  body: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.lg,
  },
  loadingDonut: {
    alignSelf: 'center',
    width: 140,
  },
  legend: {
    flex: 1,
    gap: 6,
    minWidth: 0,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: tokens.borderRadius.full,
  },
  legendLabel: {
    ...tokens.typography.label.medium,
    color: tokens.colors.slate[400],
    flex: 1,
  },
  legendCount: {
    ...tokens.typography.label.medium,
    fontWeight: '600',
    color: tokens.colors.neutral[800],
    fontVariant: ['tabular-nums'],
  },
});
